import { useMemo, Suspense, useState, useEffect } from "react";
import { Box, Grid2, Paper } from "@mui/material";
import MButton from "../../../components/inputs/yup/materialui/mButton";
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { useDataFetch } from "../../../hooks/useDataFetch2";
import { useColumns } from "../utils/columns";
import { exportExcel } from "../../../utils/exportExcel";
import Tabla from "../../../components/ui/materialui/serverSideTable";
import TableHeader from "../../../components/ui/materialui/tableHeader";
import PaidTwoToneIcon from '@mui/icons-material/PaidTwoTone';
import RestoreTwoToneIcon from '@mui/icons-material/RestoreTwoTone';
import EmergencyIcon from '@mui/icons-material/Emergency';
import ModalRecaudacion from "./modalRecaudacion";
import { useRecaudacionIndividual } from "../hooks/useRecaudacionIndividual";
import { RecaudacionReadDto, RecaudacionReversarReadDto } from "../models/mutuoRecaudar";
import { KeyboardControlKey } from "@mui/icons-material";
import { set } from "date-fns";
import Grid from '@mui/material/Grid2';
import { StatusCode, TipoFuncionalidad } from "../../../utils/enums";
import ModalDeshacerRecaudacion from "./modalDeshacerRecaudacion";
import { ModalExtincion } from "./modalExtincion";
import { ExtincionDto } from "../models/extincionDto";
import { useGenerarDiv } from "../hooks/useGenerarDiv";
import { useToast } from "../../../hooks/useToast";
import { descargarReporteDivVigente } from "../../detalleMutuo/services/servicesReportes";
import { PermissionGuard } from "../../../components/auth/ProtectedRoute";

interface IProps {
    idMutuo: number;
    setShowGenerarDividendo: (show: boolean) => void;
    isDesactivado?: boolean;
}

function Dividendos({ idMutuo, setShowGenerarDividendo, isDesactivado }: IProps) {
    const urlTabla = 'siscred-api-cartera-total/v1/dividendos/listar';
    const [showModalRecaudacion, setShowModalRecaudacion] = useState<boolean>(false);
    const [showModalDeshacerRecaudacion, setShowModalDeshacerRecaudacion] = useState<boolean>(false);
    const [showModalExtincion, setShowModalExtincion] = useState<boolean>(false);
    const [mutuoRecaudar, setMutuoRecaudar] = useState<RecaudacionReadDto | null>(null);
    const [mutuoDeshacerRecaudacion, setMutuoDeshacerRecaudacion] = useState<RecaudacionReversarReadDto | null>(null);
    const [esRecaudable, setEsRecaudable] = useState<boolean>(false);
    const { obtenerMutuoRecaudar, obtenerMutuoDeshacerRecaudacion, obtenerEsMutuoRecaudable } = useRecaudacionIndividual();
    const { extinguir } = useGenerarDiv();
    const { addToast } = useToast();

    useEffect(() => {
        const verificarRecaudable = async () => {
            const esRecaudable = await obtenerEsMutuoRecaudable(idMutuo);
            setEsRecaudable(esRecaudable === true);
        };
        verificarRecaudable();
    }, [idMutuo]);

    const handleClickRecaudacion = async () => {
        const response = await obtenerMutuoRecaudar(idMutuo);
        if (response) {
            setMutuoRecaudar(response);
            setShowModalRecaudacion(true);
        }
    };

    const handleClickDeshacerRecaudacion = async () => {
        const response = await obtenerMutuoDeshacerRecaudacion(idMutuo);
        if (response) {
            setMutuoDeshacerRecaudacion(response);
            setShowModalDeshacerRecaudacion(true);
        }
    };

    const controlTabla = useDataFetch(
        urlTabla,
        new URLSearchParams({ idMutuo: idMutuo.toString() }),
        true,
    )

    const { COLUMNS } = useColumns({ controlTabla });
    const columns = useMemo(() => COLUMNS, [controlTabla]);

    const handleRecaudacionSuccess = () => {
        controlTabla.fetchData(0); // Refrescar la tabla
    };

    const handleReversarRecaudacionSuccess = () => {
        controlTabla.fetchData(0); // Refrescar la tabla
    };

    const handleImprimirUltimoDividendo = async () => {
        await descargarReporteDivVigente(idMutuo);
    }

    const handleExtincion = async (formulario: ExtincionDto) => {
        setShowModalExtincion(false);
        const response = await extinguir(formulario, idMutuo);
        if (response.status === 200 || response.status === 201) {
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Mutuo extinguido correctamente',
            });
            controlTabla.fetchData(0);
            setEsRecaudable(false);
        }
    }

    return (
        <>
            <ModalRecaudacion
                show={showModalRecaudacion}
                setShow={setShowModalRecaudacion}
                mutuoRecaudacion={mutuoRecaudar}
                onHide={() => {
                    setShowModalRecaudacion(false);
                    setMutuoRecaudar(null);
                }}
                onRecaudacionSuccess={handleRecaudacionSuccess}
            />

            <ModalDeshacerRecaudacion
                show={showModalDeshacerRecaudacion}
                setShow={setShowModalDeshacerRecaudacion}
                mutuoRecaudacion={mutuoDeshacerRecaudacion}
                onHide={() => {
                    setShowModalDeshacerRecaudacion(false);
                    setMutuoDeshacerRecaudacion(null);
                }}
                onReversarRecaudacionSuccess={handleReversarRecaudacionSuccess}
            />

            <ModalExtincion
                show={showModalExtincion}
                onClose={() => { setShowModalExtincion(false) }}
                onSubmit={handleExtincion}
            />

            <Suspense>
                <Box sx={{ mt: 2 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Tabla
                            columns={columns}
                            control={controlTabla}
                            url={urlTabla}
                            titulo={
                                <TableHeader
                                    title=""
                                    control={controlTabla}
                                    button={
                                        <PermissionGuard permission={TipoFuncionalidad.Cartera_adm_dividendos}>
                                            {esRecaudable && (
                                                <Grid2 container spacing={1} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                                    <MButton
                                                        variant="contained"
                                                        startIcon={<PaidTwoToneIcon />}
                                                        disabled={isDesactivado}
                                                        onClick={() => {
                                                            handleClickRecaudacion();
                                                        }}>
                                                        Recaudar
                                                    </MButton>
                                                    <MButton
                                                        variant="contained"
                                                        startIcon={<RestoreTwoToneIcon />}
                                                        disabled={isDesactivado}
                                                        onClick={() => {
                                                            handleClickDeshacerRecaudacion();
                                                        }}>
                                                        Reversar Recaudación
                                                    </MButton>
                                                </Grid2>
                                            )}
                                        </PermissionGuard>
                                    }
                                    buttonExport={
                                        <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                            <MButton
                                                type="button"
                                                variant="outlined"
                                                onClick={() => exportExcel(controlTabla.data, columns, "DIVIDENDOS")}
                                                startIcon={<SummarizeTwoToneIcon />}
                                            >
                                                Exportar
                                            </MButton>
                                        </PermissionGuard>
                                    }
                                    component={
                                        <Grid2 container spacing={1} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                            <PermissionGuard permission={TipoFuncionalidad.Extinguir_mutuo}>
                                                <MButton
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<EmergencyIcon />}
                                                    disabled={isDesactivado}
                                                    onClick={() => {
                                                        setShowModalExtincion(true)
                                                    }}>
                                                    Extinguir
                                                </MButton>
                                            </PermissionGuard>

                                            <PermissionGuard permissions={[TipoFuncionalidad.Cartera_ver, TipoFuncionalidad.Cartera_adm_dividendos]}>
                                                <MButton
                                                    variant="contained"
                                                    disabled={isDesactivado}
                                                    onClick={() => {
                                                        handleImprimirUltimoDividendo();
                                                    }}>
                                                    Imprimir dividendo vigente
                                                </MButton>
                                            </PermissionGuard>

                                            <PermissionGuard permission={TipoFuncionalidad.Cartera_gen_dividendos}>
                                                <MButton
                                                    variant="contained"
                                                    disabled={isDesactivado}
                                                    onClick={() => {
                                                        setShowGenerarDividendo(true);
                                                    }}>
                                                    Generar dividendo
                                                </MButton>
                                            </PermissionGuard>
                                        </Grid2>
                                    }
                                />
                            }
                        />
                    </Paper>
                </Box>
            </Suspense>
        </>
    );
}

export default Dividendos;
